<script setup>
import { computed, ref, watchEffect } from "vue";
import { generateClient } from "aws-amplify/api";
import { uploadData } from "aws-amplify/storage";
import { Plus, Trash2 } from "lucide-vue-next";
import { createReunionSignUp } from "@/graphql/mutations";

const client = generateClient();

const primaryInfo = ref(["", "", "", "", ""]);
const groupMembers = ref([{ name: "", age: "", banquetOnly: false }]);
const selectedBranch = ref("");
const patronEnabled = ref(false);
const patronSelect = ref(null);
const patronName = ref("");
const patronEmail = ref("");
const patronDescription = ref("");
const adEnabled = ref(false);
const selectedAd = ref(null);
const adName = ref("");
const adEmail = ref("");
const adDescription = ref("");
const adFile = ref(null);
const submitting = ref(false);
const message = ref("");
const errorMessage = ref("");

const primaryInputs = [
  {
    title: "Full Name",
    type: "text",
  },
  {
    title: "Email Address",
    type: "email",
  },
  {
    title: "Mailing Address",
    type: "text",
  },
  {
    title: "Phone Number",
    type: "tel",
  },
  {
    title: "Age",
    type: "number",
  },
];

const branches = ["Mary", "Charles", "Harriet", "Perry", "James", "Rachel", "Stephen Jr.", "Wright"];
const patronLevels = ["Platinum", "Gold", "Silver", "Bronze", "Patron"];
const ads = ["Full Page", "Half Page", "Quarter Page"];

const canAddMember = computed(() => groupMembers.value.length < 11);

function addMember() {
  if (canAddMember.value) {
    groupMembers.value.push({ name: "", age: "", banquetOnly: false });
  }
}

function removeMember(index) {
  if (index > 0) {
    groupMembers.value.splice(index, 1);
  }
}

function setPatron(level) {
  patronSelect.value = patronSelect.value === level ? null : level;
}

function setAd(type) {
  selectedAd.value = selectedAd.value === type ? null : type;
}

function setAdFile(event) {
  adFile.value = event.target.files[0] || null;
}

function buildGroupMembers() {
  return groupMembers.value
    .filter((item) => item.name || item.age)
    .map((item) => `${item.name} [${item.age}], BanquetOnly: ${item.banquetOnly} | `);
}

function validateForm() {
  errorMessage.value = "";

  const missingPrimary = primaryInfo.value.some((value) => value === undefined || value === "");

  if (missingPrimary) {
    errorMessage.value = "Please complete all primary registration holder fields.";
    return false;
  }

  if (selectedBranch.value === "") {
    errorMessage.value = "Please select a family branch.";
    return false;
  }

  if (patronEnabled.value && (patronSelect.value === null || patronName.value === "" || patronEmail.value === "")) {
    errorMessage.value = "Please complete the patron level, name, and email address.";
    return false;
  }

  if (adEnabled.value && (selectedAd.value === null || adName.value === "" || adEmail.value === "")) {
    errorMessage.value = "Please complete the ad size, contact name, and contact email address.";
    return false;
  }

  return true;
}

async function uploadAdFile() {
  if (!adEnabled.value || adFile.value === null) {
    return null;
  }

  const key = `${adEmail.value || primaryInfo.value[1]}-adfile`;

  await uploadData({
    key,
    data: adFile.value,
  }).result;

  return key;
}

function clearPatronDetails() {
  if (patronEnabled.value) {
    return;
  }

  patronSelect.value = null;
  patronName.value = "";
  patronEmail.value = "";
  patronDescription.value = "";
}

function clearAdDetails() {
  if (adEnabled.value) {
    return;
  }

  selectedAd.value = null;
  adName.value = "";
  adEmail.value = "";
  adDescription.value = "";
  adFile.value = null;
}

async function submitRecovery() {
  clearPatronDetails();
  clearAdDetails();

  if (!validateForm()) {
    return;
  }

  submitting.value = true;
  message.value = "";
  errorMessage.value = "";

  try {
    const adFileName = await uploadAdFile();

    await client.graphql({
      query: createReunionSignUp,
      variables: {
        input: {
          PrimaryFullName: primaryInfo.value[0],
          PrimaryEmail: primaryInfo.value[1],
          PrimryAddress: primaryInfo.value[2],
          PrimaryPhone: primaryInfo.value[3],
          PrimaryAge: primaryInfo.value[4],
          GroupMembers: buildGroupMembers(),
          FamilyBranch: selectedBranch.value,
          PatronLevel: patronSelect.value,
          PatronName: patronName.value,
          PatronEmail: patronEmail.value,
          PatronDescription: patronDescription.value,
          AdType: selectedAd.value,
          AdName: adName.value,
          AdEmail: adEmail.value,
          AdDescription: adDescription.value,
          AdFileName: adFileName,
        },
      },
    });

    message.value = "Your registration details were submitted successfully.";
  } catch (error) {
    console.error(error);
    errorMessage.value = "We could not submit your registration details. Please try again or contact the reunion team.";
  } finally {
    submitting.value = false;
  }
}

watchEffect(() => {
  groupMembers.value[0].name = primaryInfo.value[0];
  groupMembers.value[0].age = primaryInfo.value[4];
});
</script>

<template>
  <main>
    <form class="recovery-form" @submit.prevent="submitRecovery">
      <header class="form-header">
        <img src="@/assets/sjslogo2.svg" alt="" />
        <div>
          <p class="eyebrow">2026 Family Reunion</p>
          <h1>Registration Details</h1>
        </div>
      </header>

      <section class="panel">
        <div class="section-heading">
          <h2>Primary Registration Holder</h2>
        </div>
        <div class="field-grid">
          <label v-for="(p, index) in primaryInputs" :key="p.title" class="field" :class="{ wide: index === 2 }">
            <span>{{ p.title }}</span>
            <input :type="p.type" v-model="primaryInfo[index]" />
          </label>
        </div>
      </section>

      <section class="panel">
        <div class="section-heading row-heading">
          <h2>Group Members</h2>
          <button type="button" class="icon-button add-button" @click="addMember" :disabled="!canAddMember" aria-label="Add member">
            <Plus :size="18" />
            <span>Add</span>
          </button>
        </div>

        <div class="member-list">
          <div class="member-row" v-for="(member, index) in groupMembers" :key="index">
            <label class="field member-name">
              <span>Name {{ index + 1 }}</span>
              <input type="text" v-model="member.name" :readonly="index === 0" />
            </label>
            <label class="field member-age">
              <span>Age</span>
              <input type="number" v-model="member.age" :readonly="index === 0" />
            </label>
            <label class="check-field">
              <input type="checkbox" v-model="member.banquetOnly" />
              <span>Banquet Only</span>
            </label>
            <button
              type="button"
              class="icon-only"
              @click="removeMember(index)"
              :disabled="index === 0"
              :aria-label="`Remove member ${index + 1}`"
            >
              <Trash2 :size="18" />
            </button>
          </div>
        </div>
      </section>

      <section class="panel">
        <div class="section-heading">
          <h2>Family Branch</h2>
        </div>
        <div class="segmented-grid">
          <button
            v-for="branch in branches"
            :key="branch"
            type="button"
            class="segment"
            :class="{ selected: selectedBranch === branch }"
            @click="selectedBranch = selectedBranch === branch ? '' : branch"
          >
            {{ branch }}
          </button>
        </div>
      </section>

      <section class="panel optional-panel">
        <label class="toggle-row">
          <input type="checkbox" v-model="patronEnabled" />
          <span>Include patron details</span>
        </label>

        <div v-if="patronEnabled" class="optional-content">
          <div class="segmented-grid compact">
            <button
              v-for="level in patronLevels"
              :key="level"
              type="button"
              class="segment"
              :class="{ selected: patronSelect === level }"
              @click="setPatron(level)"
            >
              {{ level }}
            </button>
          </div>
          <div class="field-grid">
            <label class="field">
              <span>Patron Name</span>
              <input type="text" v-model="patronName" />
            </label>
            <label class="field">
              <span>Patron Email</span>
              <input type="email" v-model="patronEmail" />
            </label>
            <label class="field wide">
              <span>Listing Details</span>
              <textarea v-model="patronDescription"></textarea>
            </label>
          </div>
        </div>
      </section>

      <section class="panel optional-panel">
        <label class="toggle-row">
          <input type="checkbox" v-model="adEnabled" />
          <span>Include booklet ad details</span>
        </label>

        <div v-if="adEnabled" class="optional-content">
          <div class="segmented-grid compact">
            <button
              v-for="ad in ads"
              :key="ad"
              type="button"
              class="segment"
              :class="{ selected: selectedAd === ad }"
              @click="setAd(ad)"
            >
              {{ ad }}
            </button>
          </div>
          <div class="field-grid">
            <label class="field">
              <span>Contact Name</span>
              <input type="text" v-model="adName" />
            </label>
            <label class="field">
              <span>Contact Email</span>
              <input type="email" v-model="adEmail" />
            </label>
            <label class="field wide">
              <span>Ad Details</span>
              <textarea v-model="adDescription"></textarea>
            </label>
            <label class="file-field wide">
              <span>Ad File</span>
              <input type="file" @change="setAdFile" />
            </label>
          </div>
        </div>
      </section>

      <p v-if="errorMessage" class="status error">{{ errorMessage }}</p>
      <p v-if="message" class="status success">{{ message }}</p>

      <button type="submit" class="submit" :disabled="submitting">
        <span v-if="!submitting">Submit Registration Details</span>
        <span v-else>Submitting...</span>
      </button>
    </form>
  </main>
</template>

<style scoped>
main {
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  padding: 24px;
  color: #1f2a28;
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.recovery-form {
  width: min(960px, 100%);
  margin: 0 auto;
}

.form-header {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 18px;
}

.form-header img {
  width: 72px;
  height: 72px;
  object-fit: contain;
}

.eyebrow {
  margin: 0 0 4px;
  color: #65706d;
  font-size: 0.86rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

h1,
h2 {
  margin: 0;
  font-weight: 700;
  letter-spacing: 0;
}

h1 {
  font-size: 1.8rem;
  line-height: 1.12;
}

h2 {
  font-size: 1rem;
}

.panel {
  padding: 18px;
  margin-bottom: 12px;
  border: 1px solid #dce3e1;
  border-radius: 8px;
  background: #fff;
}

.section-heading {
  margin-bottom: 14px;
}

.row-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.field,
.file-field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
}

.field span,
.file-field span {
  color: #3f4b48;
  font-size: 0.84rem;
  font-weight: 700;
}

.wide {
  grid-column: 1 / -1;
}

input,
textarea {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  border: 1px solid #cbd6d3;
  border-radius: 6px;
  background: #fff;
  color: #1f2a28;
  font: inherit;
}

input {
  min-height: 40px;
  padding: 0 11px;
}

textarea {
  min-height: 86px;
  padding: 10px 11px;
  resize: vertical;
}

input:focus,
textarea:focus {
  border-color: #38413f;
  outline: 3px solid rgba(47, 111, 96, 0.14);
}

input[readonly] {
  background: #f2f5f4;
  color: #596562;
}

button {
  font: inherit;
}

.icon-button,
.icon-only,
.segment,
.submit {
  border: 0;
  cursor: pointer;
}

.icon-button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 36px;
  padding: 0 12px;
  border-radius: 6px;
  background: #38413f;
  color: #fff;
  font-weight: 700;
}

.icon-only {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background: #edf2f1;
  color: #4a5552;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.member-list {
  display: grid;
  gap: 10px;
}

.member-row {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) 88px 150px 40px;
  gap: 10px;
  align-items: end;
}

.check-field,
.toggle-row {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  color: #2d3835;
  font-size: 0.92rem;
  font-weight: 700;
}

.check-field {
  min-height: 40px;
}

.check-field input,
.toggle-row input {
  width: 18px;
  height: 18px;
  min-height: 18px;
  accent-color: #38413f;
}

.segmented-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.segmented-grid.compact {
  margin: 14px 0;
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.segment {
  min-height: 38px;
  padding: 0 10px;
  border: 1px solid #cbd6d3;
  border-radius: 6px;
  background: #f8faf9;
  color: #2d3835;
  font-weight: 700;
}

.segment.selected {
  border-color: #38413f;
  background: #38413f;
  color: #fff;
}

.optional-panel {
  padding: 14px 18px;
}

.optional-content {
  padding-top: 2px;
}

.file-field input {
  padding: 9px 11px;
}

.status {
  margin: 12px 0;
  padding: 12px 14px;
  border-radius: 6px;
  line-height: 1.4;
  font-weight: 700;
}

.error {
  background: #feecec;
  color: #842626;
}

.success {
  background: #e8f7ef;
  color: #1d6b43;
}

.submit {
  width: 100%;
  min-height: 48px;
  margin-top: 4px;
  border-radius: 8px;
  background: #38413f;
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
}

@media screen and (max-width: 760px) {
  main {
    padding: 16px;
  }

  .form-header {
    align-items: flex-start;
  }

  .form-header img {
    width: 58px;
    height: 58px;
  }

  h1 {
    font-size: 1.45rem;
  }

  .field-grid,
  .member-row,
  .segmented-grid,
  .segmented-grid.compact {
    grid-template-columns: 1fr;
  }

  .member-row {
    padding-bottom: 12px;
    border-bottom: 1px solid #edf1f0;
  }

  .member-row:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }

  .check-field,
  .icon-only {
    align-self: start;
  }
}
</style>
